import { Vector2 } from "three";
import { MathX } from "../../../math/MathX";
import { AudioBufferChannelReader } from "../../AudioBufferChannelReader";
import { WaveformGenerationOptions } from "./WaveFormGenerationOptions";
import { WaveFormGenerator } from "./WaveFormGenerator";
import { WaveLevels } from "./WaveLevels";

export class WaveForm
{
  private _original:AudioBuffer;

  get original()
  {
    return this._original; 
  }

  private _min:WaveLevels;
  get min(){ return this._min; }

  private _max:WaveLevels;
  get max(){ return this._max; }

  private _normalizer:number;

  private _originalReader:AudioBufferChannelReader;
  get reader(){ return this._originalReader }

  get numSamples()
  {
    return this._original.getChannelData( 0 ).length;
  }

  constructor( original:AudioBuffer, min:WaveLevels, max:WaveLevels, reader:AudioBufferChannelReader )
  {
    this._original = original;
    this._min = min;
    this._max = max;
    this._normalizer = 1.0 / original.getChannelData( 0 ).length;
    this._originalReader = reader;
  }

  getPoints( startPositionNormalized:number, endPositionNormalized:number ):Vector2[]
  {
    let startPosition = startPositionNormalized / this._normalizer;
    let endPosition   = endPositionNormalized / this._normalizer;

    let points:Vector2[] = [];

    let p0 = Math.floor( startPosition );
    let p1 = Math.ceil( endPosition );
    

    if ( p1 < p0 )
    {
      let b = p1;
      p1 = p0;
      p0 = b;
    }

    for ( let i = p0; i <= p1; i++ )
    {
      let p = new Vector2();
      p.x = i * this._normalizer;
      p.y = this._originalReader.sample( this._original, i );

      points.push( p );
    }

    return points;
  }

  sampleDataNormalized( startPositionNormalized:number, endPositionNormalized:number, dataContainer:Vector2[], scale:number = 1, samplingLevelOffset:number = 0 )
  {
    let startPosition = startPositionNormalized / this._normalizer;
    let endPosition   = endPositionNormalized / this._normalizer;

    this.sampleData( startPosition, endPosition, dataContainer, scale, samplingLevelOffset );
  }

  getSamplesPerEntryNormalized( startPositionNormalized:number, endPositionNormalized:number, dataContainer:Vector2[] )
  {
    let startPosition = startPositionNormalized / this._normalizer;
    let endPosition   = endPositionNormalized / this._normalizer;

    return this.getSamplesPerEntry( startPosition, endPosition, dataContainer );
  }

  getSamplesPerEntry( startPosition:number, endPosition:number, dataContainer:Vector2[] )
  {
    let numSamples      = endPosition - startPosition;
    let samplesPerEntry = numSamples / dataContainer.length;

    return samplesPerEntry;
  }

  sampleData( startPosition:number, endPosition:number, dataContainer:Vector2[], scale:number = 1, samplingLevelOffset:number )
  {
    let numSamples      = endPosition - startPosition;
    let samplesPerEntry = numSamples / dataContainer.length;

    let rangeNormalizer = scale / 127.0;
    let rangeNormalizerRaw = scale;

    let levelIndex = WaveLevels.resolutionToLevelIndex( samplesPerEntry ) + 1 + samplingLevelOffset;

    let level = Math.floor( levelIndex ); 
    let levelAbove = level + 1;

   

    let levelLerp = levelIndex - level;

    // testing 
    //level = 0;
    //rangeNormalizer = 1;

    console.log( "sampling at", { levelIndex, samplesPerEntry, numSamples, rangeNormalizer } );

    

    let min = this._min.get( level );
    let max = this._max.get( level );

    let minAbove = this._min.get( levelAbove );
    let maxAbove = this._max.get( levelAbove );

    let startNormalized = startPosition * this._normalizer;
    let endNormalized   = endPosition * this._normalizer;

    let iToState = 1 / dataContainer.length;
    
    for ( let i = 0; i < dataContainer.length; i++ )
    {
      let state = i * iToState;
      let normalizedPosition = MathX.lerp( startNormalized, endNormalized, state );

      let minValue = min.sample( normalizedPosition );
      let maxValue = max.sample( normalizedPosition );

      let minValueAbove = minAbove.sample( normalizedPosition );
      let maxValueAbove = maxAbove.sample( normalizedPosition );


      dataContainer[ i ].x = MathX.lerp( minValue, minValueAbove, levelLerp ) * rangeNormalizer;
      dataContainer[ i ].y = MathX.lerp( maxValue, maxValueAbove, levelLerp ) * rangeNormalizer;      
    }

    if ( levelIndex < 1 )
    {
      var amount = Math.max( 0, levelIndex * 2 - 1 );  
      var amount2 = 1 - amount;

      console.log( "Weights interpolated sample view: ", amount, amount2 );

      let start = startNormalized * this._original.getChannelData( 0 ).length;
      let end   = endNormalized   * this._original.getChannelData( 0 ).length;

      for ( let i = 0; i < dataContainer.length; i++ )
      {
        let state = i * iToState;
        let normalizedPosition = MathX.lerp( start, end, state );

        let sample = this._originalReader.sampleLerp( this._original, normalizedPosition ) * amount2 * rangeNormalizerRaw;

        dataContainer[ i ].multiplyScalar( amount );

        dataContainer[ i ].x += sample;
        dataContainer[ i ].y += sample;
      }
    }
  }

  static loadFromBuffer( arraybuffer:ArrayBuffer, audioCtx:AudioContext, options:WaveformGenerationOptions ):Promise<WaveForm[]>
  {
    let promise = new Promise<WaveForm[]>(
      ( resolve, reject )=>
      {


      audioCtx.decodeAudioData( arraybuffer,
        async ( buffer ) => 
        {
          let waveforms = await WaveFormGenerator.generate( buffer, options );

          resolve( waveforms );
        },

        reject
      );

      } 
    );


    return Promise.resolve( promise );


  }

  static loadFromURL( path:string, audioCtx:AudioContext, options:WaveformGenerationOptions ):Promise<WaveForm[]>
  {
    let promise = new Promise<WaveForm[]>(
      ( resolve, reject )=>
      {
        let request = new XMLHttpRequest();
  
        request.open( "GET", path, true);
      
        request.responseType = "arraybuffer";
        
        let onError = ( e:any )=>
        {
          console.log( "Couldn't load", path );
          console.error( e );     
          
          reject();
        }

        request.onload = 
          () => 
          { 
            if ( request.status !== 200 )
            {
              onError( 
                {
                  error: "Error State",
                  state: request.readyState, status:request.status, 
                  response: new TextDecoder().decode( request.response ) 
                } 
              );
              
              return;
            }

            let audioData = request.response;
            
        
            audioCtx.decodeAudioData(  audioData,
              async ( buffer ) => 
              {
                let waveforms = await WaveFormGenerator.generate( buffer, options );

                resolve( waveforms );
              },

              onError
            );
        };

        request.onerror = onError;
        request.onabort = onError;
      
        request.send();
      }
    );

    return promise;

   
    
  }
}